/**
 * ActivityTable Component
 *
 * "Latest Activity" table - new listings, price changes, pending, and sold.
 * Fetches one batch per day-range selection and sorts/filters entirely
 * client-side (no re-fetch on sort/filter clicks) - the widest real-world
 * scope (a full year for the busiest region) is ~1,500 rows, small enough
 * to hold in memory and re-sort instantly.
 */
import { injectStyles } from './styles.js';
// Shortened from "New Listing"/"Price Change" to make room for a 5th chip
// (Cancelled) without crowding the filter row.
const TYPE_LABEL = {
    new_listing: 'New',
    price_change: 'Change',
    pending: 'Pending',
    sold: 'Sold',
    cancelled: 'Cancelled',
};
const TYPE_ICON = {
    new_listing: '🆕',
    price_change: '💲',
    pending: '🕓',
    sold: '✅',
    cancelled: '🚫',
};
const ALL_TYPES = Object.keys(TYPE_LABEL);
// Cancelled starts unchecked - it's the one type that isn't actionable
// (nothing to click into, no extra price detail), so it defaults out of
// the way rather than cluttering a feed otherwise made of live/actionable
// changes. Still toggleable on manually.
const DEFAULT_ACTIVE_TYPES = ALL_TYPES.filter(t => t !== 'cancelled');
// Only these three represent a listing still For Sale or Pending - Sold
// and Cancelled rows aren't clickable (nothing live to open for either).
const CLICKABLE_TYPES = new Set(['new_listing', 'price_change', 'pending']);
const DAY_OPTIONS = [7, 30, 90, 365];
export class ActivityTable {
    options;
    container;
    items = [];
    hasMore = false;
    currentDays = 30;
    sortField = 'date';
    sortDirection = 'desc';
    activeTypes = new Set(DEFAULT_ACTIVE_TYPES);
    loaded = false;
    loading = false;
    // Generic hook for a caller-supplied filter (e.g. the agent app's
    // Region/District/Neighborhood cascade) layered on top of the built-in
    // type filter. Deliberately not baked in as its own UI here - a 3-level
    // geography cascade only makes sense for an unscoped, islandwide table;
    // GeographyModal's usage is already scoped to one district/region and
    // doesn't need it. Re-applies to whatever's already loaded, no re-fetch.
    externalFilter = null;
    constructor(options) {
        this.options = options;
        this.container = options.container;
        injectStyles();
    }
    setExternalFilter(predicate) {
        this.externalFilter = predicate;
        if (this.loaded)
            this.renderShell();
    }
    /** Idempotent - safe to call every time the tab is switched to. */
    async load() {
        if (this.loaded || this.loading)
            return;
        await this.fetchAndRender();
    }
    async fetchAndRender() {
        this.loading = true;
        this.renderShell();
        try {
            const data = await this.options.onLoadActivity(this.currentDays);
            this.items = data?.items ?? [];
            this.hasMore = data?.hasMore ?? false;
            this.loaded = true;
        }
        catch (error) {
            console.error('[ActivityTable] Failed to load activity:', error);
            this.items = [];
            this.hasMore = false;
        }
        finally {
            this.loading = false;
            this.renderShell();
        }
    }
    renderShell() {
        this.container.innerHTML = `
      <div class="sm-activity-controls">
        <div class="sm-activity-days">
          ${DAY_OPTIONS.map(d => `
            <button type="button" class="sm-activity-day-btn ${d === this.currentDays ? 'active' : ''}" data-days="${d}">
              ${d < 365 ? `${d}d` : '1yr'}
            </button>
          `).join('')}
        </div>
        <div class="sm-activity-types">
          ${Object.keys(TYPE_LABEL).map(t => `
            <button type="button" class="sm-activity-type-chip ${this.activeTypes.has(t) ? 'active' : ''}" data-type="${t}">
              ${TYPE_ICON[t]} ${TYPE_LABEL[t]}
            </button>
          `).join('')}
        </div>
      </div>
      <div class="sm-activity-table-wrap">
        ${this.loading ? this.renderLoading() : this.renderTable()}
      </div>
    `;
        this.attachListeners();
    }
    renderLoading() {
        return `<div class="sm-activity-empty"><div class="sm-activity-spinner"></div><p>Loading recent activity…</p></div>`;
    }
    renderTable() {
        const filtered = this.items.filter(i => this.activeTypes.has(i.type) && (!this.externalFilter || this.externalFilter(i)));
        const sorted = this.sortItems(filtered);
        if (sorted.length === 0) {
            return `
        <div class="sm-activity-empty">
          <p>No activity in the last ${this.currentDays < 365 ? `${this.currentDays} days` : 'year'}${(this.activeTypes.size < DEFAULT_ACTIVE_TYPES.length || this.externalFilter) ? ' matching the selected filters' : ''}.</p>
        </div>
      `;
        }
        const showNeighborhood = !!this.options.showNeighborhoodColumn;
        const arrow = (field) => this.sortField === field ? (this.sortDirection === 'asc' ? ' ▲' : ' ▼') : '';
        return `
      <table class="sm-activity-table">
        <thead>
          <tr>
            <th class="sortable" data-sort="date">Date${arrow('date')}</th>
            <th class="sortable" data-sort="type">Type${arrow('type')}</th>
            <th>Address</th>
            ${showNeighborhood ? '<th>Neighborhood</th>' : ''}
            <th class="sortable numeric" data-sort="price">Price${arrow('price')}</th>
          </tr>
        </thead>
        <tbody>
          ${sorted.map(item => this.renderRow(item, showNeighborhood)).join('')}
        </tbody>
      </table>
      ${this.hasMore ? `
        <div class="sm-activity-more-note">
          Showing the most recent ${sorted.length} results${this.activeTypes.size < DEFAULT_ACTIVE_TYPES.length ? ' (of the loaded batch)' : ''} for this range - narrow the date range to see everything.
        </div>
      ` : ''}
    `;
    }
    renderRow(item, showNeighborhood) {
        let priceCell = formatPrice(item.listPrice);
        if (item.type === 'price_change' && item.priceChange != null) {
            const down = item.priceChange < 0;
            const pct = item.priceChangePercent != null
                ? ` (${item.priceChangePercent > 0 ? '+' : ''}${item.priceChangePercent.toFixed(1)}%)`
                : '';
            priceCell = `
        ${formatPrice(item.listPrice)}
        <div class="sm-activity-price-delta ${down ? 'down' : 'up'}">${down ? '▼' : '▲'} ${formatPrice(Math.abs(item.priceChange))}${pct}</div>
      `;
        }
        else if (item.type === 'sold' && item.closePrice != null) {
            // Both prices, not just the sold price - the gap between ask and
            // sale (over or under list) is the thing worth seeing at a glance.
            priceCell = `
        ${formatPrice(item.closePrice)}
        <div class="sm-activity-price-sold-detail">List: ${formatPrice(item.listPrice)}</div>
      `;
        }
        // Only New/Change/Pending are still-live listings worth opening -
        // Sold/Cancelled render as plain text, no link, no listener attached
        // (attachListeners only queries .sm-activity-address).
        const addressHtml = CLICKABLE_TYPES.has(item.type)
            ? `<a href="#" class="sm-activity-address" data-listing-id="${escapeAttr(item.listingId)}">${escapeHtml(item.address || 'Address unavailable')}</a>`
            : `<span class="sm-activity-address-static">${escapeHtml(item.address || 'Address unavailable')}</span>`;
        // Opt-in Cart toggle (agent app's Market Activity page only - see
        // ActivityTableOptions' comment) - shown for every row regardless of
        // type, unlike the address link above: a sold/cancelled comp is
        // still reasonable to cart for reference, even with no live page to
        // click through to. Same icon/markup as PropertyModal's own Agent
        // Pick button (packages/ui-components/src/PropertyModal/
        // PropertyModal.ts), copied verbatim for visual consistency.
        const agentPickHtml = (this.options.showAgentPickButton && item.listingDbId)
            ? (() => {
                const isPicked = this.options.isAgentPicked?.(item.listingDbId) ?? false;
                return `
            <button class="sm-activity-agent-pick${isPicked ? ' active' : ''}" aria-label="${isPicked ? 'Remove from cart' : 'Add to cart'}" title="${isPicked ? 'Remove from cart' : 'Add to cart'}" data-listing-db-id="${escapeAttr(item.listingDbId)}" data-label="${escapeAttr(item.address || 'this property')}">
              <svg viewBox="0 0 24 24" fill="${isPicked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </button>
          `;
            })()
            : '';
        return `
      <tr>
        <td class="sm-activity-date">${formatDate(item.createdAt)}</td>
        <td><span class="sm-activity-type-badge">${TYPE_ICON[item.type]} ${TYPE_LABEL[item.type]}</span></td>
        <td>${addressHtml}${agentPickHtml}</td>
        ${showNeighborhood ? `<td>${escapeHtml(item.neighborhoodName || '-')}</td>` : ''}
        <td class="numeric">${priceCell}</td>
      </tr>
    `;
    }
    sortItems(items) {
        const dir = this.sortDirection === 'asc' ? 1 : -1;
        return [...items].sort((a, b) => {
            switch (this.sortField) {
                case 'date':
                    return dir * a.createdAt.localeCompare(b.createdAt);
                case 'type':
                    return dir * TYPE_LABEL[a.type].localeCompare(TYPE_LABEL[b.type]);
                case 'price':
                    return dir * (a.listPrice - b.listPrice);
                default:
                    return 0;
            }
        });
    }
    attachListeners() {
        this.container.querySelectorAll('.sm-activity-day-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const days = parseInt(btn.dataset.days || '30', 10);
                if (days === this.currentDays)
                    return;
                this.currentDays = days;
                this.loaded = false;
                void this.fetchAndRender();
            });
        });
        this.container.querySelectorAll('.sm-activity-type-chip').forEach(btn => {
            btn.addEventListener('click', () => {
                const type = btn.dataset.type;
                if (this.activeTypes.has(type)) {
                    // Never allow filtering down to zero types - re-enable everything
                    // instead, since an empty table with no explanation reads as broken.
                    if (this.activeTypes.size === 1) {
                        this.activeTypes = new Set(DEFAULT_ACTIVE_TYPES);
                    }
                    else {
                        this.activeTypes.delete(type);
                    }
                }
                else {
                    this.activeTypes.add(type);
                }
                this.renderShell();
            });
        });
        this.container.querySelectorAll('.sm-activity-table th.sortable').forEach(th => {
            th.addEventListener('click', () => {
                const field = th.dataset.sort;
                if (this.sortField === field) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
                }
                else {
                    this.sortField = field;
                    this.sortDirection = field === 'date' ? 'desc' : 'asc';
                }
                this.renderShell();
            });
        });
        this.container.querySelectorAll('.sm-activity-address').forEach(a => {
            a.addEventListener('click', (e) => {
                e.preventDefault();
                const listingId = a.dataset.listingId;
                if (listingId && this.options.onListingClick) {
                    this.options.onListingClick(listingId);
                }
            });
        });
        this.container.querySelectorAll('.sm-activity-agent-pick').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const el = btn;
                const listingDbId = el.dataset.listingDbId;
                const label = el.dataset.label || 'this property';
                if (!listingDbId || !this.options.onAgentPickToggle)
                    return;
                // Optimistic DOM toggle, not a full re-render - onAgentPickToggle's
                // underlying add/remove call is async (a real network request via
                // CartDrawer), so isAgentPicked() wouldn't reflect the change yet
                // if read synchronously right after. Same pattern PropertyModal's
                // own handleAgentPickToggle already uses for the identical reason.
                const newState = !el.classList.contains('active');
                el.classList.toggle('active', newState);
                el.setAttribute('aria-label', newState ? 'Remove from cart' : 'Add to cart');
                el.setAttribute('title', newState ? 'Remove from cart' : 'Add to cart');
                const svg = el.querySelector('svg');
                svg?.setAttribute('fill', newState ? 'currentColor' : 'none');
                this.options.onAgentPickToggle(listingDbId, newState, label);
            });
        });
    }
    destroy() {
        this.container.innerHTML = '';
    }
}
function formatPrice(n) {
    if (n == null)
        return '-';
    return '$' + Math.round(n).toLocaleString();
}
function formatDate(dateStr) {
    const date = new Date(dateStr.replace(' ', 'T') + (dateStr.includes('T') ? '' : 'Z'));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s) {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
//# sourceMappingURL=ActivityTable.js.map